import React, { useState } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Badge, ProgressBar, Alert, Spinner
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import * as XLSX from 'xlsx';
import './Produits.css';

export default function ImportProduits() {
    const navigate = useNavigate();
    const [importStep, setImportStep] = useState(1);
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState([]);
    const [validationResults, setValidationResults] = useState({});
    const [importProgress, setImportProgress] = useState(0); 
    const [importStatus, setImportStatus] = useState('idle');
    const [importStats, setImportStats] = useState({});
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Colonnes attendues pour l'import
    const expectedColumns = [
        { key: 'reference', label: 'Référence', required: false, example: 'REF2024001' },
        { key: 'designation', label: 'Désignation', required: true, example: 'Produit test' },
        { key: 'description', label: 'Description', required: false, example: 'Description du produit' },
        { key: 'prix_achat_ht', label: 'Prix achat HT', required: true, example: '1000' },
        { key: 'prix_vente_ht', label: 'Prix vente HT', required: true, example: '1500' },
        { key: 'seuil_alerte', label: 'Seuil alerte', required: false, example: '5' },
        { key: 'stock_initial', label: 'Stock initial', required: false, example: '10' }
    ];

    // Télécharger le modèle
    const downloadTemplate = () => {
        const data = [
            ['reference', 'designation', 'description', 'prix_achat_ht', 'prix_vente_ht', 'seuil_alerte', 'stock_initial'],
            ['REF001', 'Produit exemple 1', 'Description produit 1', '1000', '1500', '5', '10'],
            ['REF002', 'Produit exemple 2', 'Description produit 2', '2000', '2500', '3', '5'],
        ];

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");
        XLSX.writeFile(workbook, "modele-import-produits.xlsx");
    };

    // Gestion du fichier
    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedExtensions = ['.csv', '.xls', '.xlsx'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        
        if (!allowedExtensions.includes(fileExtension)) {
            alert('Veuillez sélectionner un fichier CSV ou Excel valide');
            return;
        }

        setSelectedFile(file);
        previewFile(file);
    };

    // Prévisualisation
    const previewFile = (file) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                let previewData = [];
                
                if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
                    const content = e.target.result;
                    const lines = content.split('\n').slice(0, 11);
                    previewData = lines.map(line => 
                        line.split(',').map(cell => cell.trim().replace(/^"|"$/g, ''))
                    );
                } else {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    previewData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }).slice(0, 11);
                }
                
                setFilePreview(previewData);
                validateFile(previewData);
                
            } catch (error) {
                console.error('Erreur lecture fichier:', error);
                alert('Erreur lors de la lecture du fichier');
            }
        };

        if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };

    // Validation
    const validateFile = (previewData) => {
        const results = {
            valid: 0,
            invalid: 0,
            errors: [],
            columnMatch: true,
            produits: []
        };

        if (previewData.length > 0) {
            const headers = previewData[0].map(h => h?.toString().toLowerCase().trim());
            
            // Vérifier les colonnes obligatoires
            const requiredHeaders = ['designation', 'prix_achat_ht', 'prix_vente_ht'];
            
            requiredHeaders.forEach(required => {
                if (!headers.includes(required)) {
                    results.columnMatch = false;
                    results.errors.push(`Colonne '${required}' manquante`);
                }
            });

            // Trouver les index des colonnes
            const colIndex = {
                reference: headers.indexOf('reference'),
                designation: headers.indexOf('designation'),
                description: headers.indexOf('description'),
                prix_achat_ht: headers.indexOf('prix_achat_ht'),
                prix_vente_ht: headers.indexOf('prix_vente_ht'),
                seuil_alerte: headers.indexOf('seuil_alerte'),
                stock_initial: headers.indexOf('stock_initial')
            };

            if (results.columnMatch) {
                previewData.slice(1).forEach((row, index) => {
                    const ligne = index + 2;
                    
                    // Vérifier les champs obligatoires
                    if (!row[colIndex.designation] || !row[colIndex.prix_achat_ht] || !row[colIndex.prix_vente_ht]) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Champs obligatoires manquants`);
                    } 
                    // Vérifier que les prix sont des nombres
                    else if (isNaN(parseFloat(row[colIndex.prix_achat_ht])) || parseFloat(row[colIndex.prix_achat_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix achat invalide`);
                    }
                    else if (isNaN(parseFloat(row[colIndex.prix_vente_ht])) || parseFloat(row[colIndex.prix_vente_ht]) <= 0) {
                        results.invalid++;
                        results.errors.push(`Ligne ${ligne}: Prix vente invalide`);
                    }
                    else {
                        // Créer l'objet produit
                        const produit = {
                            reference: colIndex.reference !== -1 ? row[colIndex.reference]?.toString().trim() || '' : '',
                            designation: row[colIndex.designation]?.toString().trim(),
                            description: colIndex.description !== -1 ? row[colIndex.description]?.toString().trim() || '' : '',
                            prix_achat_ht: parseFloat(row[colIndex.prix_achat_ht]),
                            prix_vente_ht: parseFloat(row[colIndex.prix_vente_ht]),
                            seuil_alerte: colIndex.seuil_alerte !== -1 ? parseInt(row[colIndex.seuil_alerte]) || 5 : 5,
                            stock_initial: colIndex.stock_initial !== -1 ? parseInt(row[colIndex.stock_initial]) || 0 : 0
                        };
                        
                        results.produits.push(produit);
                        results.valid++;
                    }
                });
            }
        }

        setValidationResults(results);
        setImportStep(2);
    };

    // Import réel vers l'API
    const importProduits = async () => {
        setImporting(true);
        setImportStatus('importing');
        setImportProgress(0);
        setError(null);

        try {
            const token = localStorage.getItem("token");
            
            // Simulation de progression
            const progressInterval = setInterval(() => {
                setImportProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            // Envoyer chaque produit à l'API
            const results = {
                total: validationResults.produits.length,
                imported: 0,
                failed: 0,
                errors: []
            };

            for (let i = 0; i < validationResults.produits.length; i++) {
                const produit = validationResults.produits[i];
                
                try {
                    const response = await fetch(
                        'https://sawd.julesferry-ga.com/api/produits.php',
                        {
                            method: 'POST',
                            headers: { 
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json'
                            },
                            body: JSON.stringify(produit)
                        }
                    );

                    const result = await response.json();

                    if (result.success) {
                        results.imported++;
                    } else {
                        results.failed++;
                        results.errors.push(`Produit ${produit.designation}: ${result.message || 'Erreur inconnue'}`);
                    }
                } catch (err) {
                    results.failed++;
                    results.errors.push(`Produit ${produit.designation}: Erreur de connexion`);
                }

                // Mettre à jour la progression
                setImportProgress(Math.floor(((i + 1) / validationResults.produits.length) * 100));
            }

            clearInterval(progressInterval);
            setImportProgress(100);
            
            setImportStats(results);
            setImportStatus(results.failed === 0 ? 'success' : results.imported > 0 ? 'partial' : 'error');
            
            if (results.failed === 0) {
                setSuccessMessage(`${results.imported} produits importés avec succès !`);
                setShowSuccessAlert(true);
            }

        } catch (error) {
            console.error("Erreur import:", error);
            setImportStatus('error');
            setError(error.message || "Erreur lors de l'import");
        } finally {
            setImporting(false);
            setImportStep(3);
        }
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <ol className="breadcrumb fs-sm mb-1">
                                        <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock">Stock</Link></li>
                                        <li className="breadcrumb-item"><Link to="/stock/produits">Produits</Link></li>
                                        <li className="breadcrumb-item active">Import produits</li>
                                    </ol>
                                    <h4 className="main-title mb-0">
                                        <i className="ri-upload-cloud-2-line text-primary me-2"></i>
                                        Importer des produits
                                    </h4>
                                </div>
                                <Button variant="outline-primary" onClick={downloadTemplate}>
                                    <i className="ri-download-line me-2"></i>
                                    Télécharger le modèle
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4">
                            <i className="ri-checkbox-circle-line me-2"></i>
                            {successMessage}
                        </Alert>
                    )}

                    {/* Étapes */}
                    <Row className="mb-4">
                        <Col md={12}>
                            <Card className="shadow-sm border-0">
                                <Card.Body>
                                    <div className="d-flex justify-content-between">
                                        {[1, 2, 3].map(step => (
                                            <div key={step} className="step-indicator">
                                                <div className={`step-circle ${importStep >= step ? 'active' : ''}`}>
                                                    {step}
                                                </div>
                                                <small className="text-muted">
                                                    {step === 1 && '1. Fichier'}
                                                    {step === 2 && '2. Validation'}
                                                    {step === 3 && '3. Résultats'}
                                                </small>
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Étape 1: Upload */}
                    {importStep === 1 && (
                        <Row>
                            <Col lg={8} className="mx-auto">
                                <Card className="shadow-sm border-0">
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">Sélectionner le fichier</h5>
                                    </Card.Header>
                                    <Card.Body className="p-4 text-center">
                                        <div className="upload-area p-5">
                                            <i className="ri-file-upload-line display-1 text-primary mb-3"></i>
                                            <h5>Glissez-déposez votre fichier</h5>
                                            <p className="text-muted mb-4">
                                                Formats supportés: CSV, Excel (.xlsx, .xls)
                                            </p>
                                            <Form.Group>
                                                <Form.Control
                                                    type="file"
                                                    accept=".csv,.xlsx,.xls"
                                                    onChange={handleFileSelect}
                                                    className="d-none"
                                                    id="fileInput"
                                                />
                                                <Button 
                                                    variant="primary"
                                                    onClick={() => document.getElementById('fileInput').click()}
                                                >
                                                    <i className="text-white ri-folder-open-line me-2"></i>
                                                    Parcourir
                                                </Button>
                                            </Form.Group>
                                        </div>

                                        <div className="mt-4 text-start">
                                            <h6>Format attendu :</h6>
                                            <Table striped bordered size="sm">
                                                <thead>
                                                    <tr>
                                                        <th>Colonne</th>
                                                        <th>Obligatoire</th>
                                                        <th>Exemple</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {expectedColumns.map(col => (
                                                        <tr key={col.key}>
                                                            <td><strong>{col.label}</strong></td>
                                                            <td>
                                                                {col.required ? 
                                                                    <Badge bg="danger">Obligatoire</Badge> : 
                                                                    <Badge bg="secondary">Optionnel</Badge>
                                                                }
                                                            </td>
                                                            <td><code>{col.example}</code></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Table>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 2: Validation */}
                    {importStep === 2 && (
                        <Row>
                            <Col lg={10} className="mx-auto">
                                <Card className="shadow-sm border-0">
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">Validation des données</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        {importing ? (
                                            <div className="text-center py-4">
                                                <h5 className="mb-3">Import en cours...</h5>
                                                <ProgressBar 
                                                    now={importProgress} 
                                                    label={`${importProgress}%`} 
                                                    className="mb-3"
                                                />
                                                <p className="text-muted">
                                                    Traitement de {validationResults.produits?.length || 0} produits...
                                                </p>
                                            </div>
                                        ) : (
                                            <>
                                                {/* Résultats */}
                                                <Row className="g-3 mb-4">
                                                    <Col sm={4}>
                                                        <Card className="text-center border-0 bg-success-soft">
                                                            <Card.Body>
                                                                <h2 className="text-success mb-1">{validationResults.valid || 0}</h2>
                                                                <small>Produits valides</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col sm={4}>
                                                        <Card className="text-center border-0 bg-danger-soft">
                                                            <Card.Body>
                                                                <h2 className="text-danger mb-1">{validationResults.invalid || 0}</h2>
                                                                <small>Erreurs</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    <Col sm={4}>
                                                        <Card className="text-center border-0 bg-info-soft">
                                                            <Card.Body>
                                                                <h2 className="text-info mb-1">{filePreview.length - 1 || 0}</h2>
                                                                <small>Total lignes</small>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                </Row>

                                                {/* Aperçu */}
                                                <h6 className="mb-3">Aperçu des données :</h6>
                                                <div className="table-responsive mb-4">
                                                    <Table striped size="sm">
                                                        <thead>
                                                            <tr>
                                                                {filePreview[0]?.map((h, i) => (
                                                                    <th key={i}>{h}</th>
                                                                ))}
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {filePreview.slice(1, 6).map((row, i) => (
                                                                <tr key={i}>
                                                                    {row.map((cell, j) => (
                                                                        <td key={j}>{cell || '-'}</td>
                                                                    ))}
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>

                                                {/* Erreurs */}
                                                {validationResults.errors?.length > 0 && (
                                                    <div className="mb-4">
                                                        <h6 className="text-danger">Erreurs détectées :</h6>
                                                        <div className="border rounded p-3" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                                            {validationResults.errors.slice(0, 10).map((err, i) => (
                                                                <div key={i} className="small text-danger mb-1">
                                                                    <i className="ri-close-circle-line me-1"></i>
                                                                    {err}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Actions */}
                                                <div className="d-flex justify-content-between">
                                                    <Button variant="outline-secondary" onClick={() => setImportStep(1)}>
                                                        Retour
                                                    </Button>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={importProduits}
                                                        disabled={validationResults.valid === 0 || importing}
                                                    >
                                                        {importing ? (
                                                            <>
                                                                <Spinner size="sm" className="me-2" />
                                                                Import en cours...
                                                            </>
                                                        ) : (
                                                            'Confirmer l\'import'
                                                        )}
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Étape 3: Résultats */}
                    {importStep === 3 && (
                        <Row>
                            <Col lg={8} className="mx-auto">
                                <Card className="shadow-sm border-0">
                                    <Card.Header className={`${
                                        importStatus === 'success' ? 'bg-success' : 
                                        importStatus === 'partial' ? 'bg-warning' : 'bg-danger'
                                    } text-white`}>
                                        <h5 className="mb-0">
                                            {importStatus === 'success' && 'Import réussi !'}
                                            {importStatus === 'partial' && 'Import partiel'}
                                            {importStatus === 'error' && 'Erreur'}
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="text-center p-4">
                                        {importStatus === 'success' && (
                                            <>
                                                <i className="ri-checkbox-circle-line text-success fs-48 mb-3"></i>
                                                <h4>Import terminé avec succès</h4>
                                                <p className="mb-3">{importStats.imported} produits importés</p>
                                            </>
                                        )}

                                        {importStatus === 'partial' && (
                                            <>
                                                <i className="ri-error-warning-line text-warning fs-48 mb-3"></i>
                                                <h4>Import partiel</h4>
                                                <p className="mb-3">
                                                    {importStats.imported} produits importés<br />
                                                    {importStats.failed} échecs
                                                </p>
                                                {importStats.errors?.length > 0 && (
                                                    <div className="text-start mt-3">
                                                        <h6>Détails des erreurs :</h6>
                                                        <ul className="text-danger">
                                                            {importStats.errors.slice(0, 3).map((err, i) => (
                                                                <li key={i} className="small">{err}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {importStatus === 'error' && (
                                            <>
                                                <i className="ri-error-warning-line text-danger fs-48 mb-3"></i>
                                                <h4>Une erreur est survenue</h4>
                                                <p className="text-muted mb-4">{error || "L'import n'a pas pu être effectué"}</p>
                                            </>
                                        )}

                                        <div className="d-flex gap-3 justify-content-center mt-4">
                                            <Button variant="outline-primary" onClick={() => navigate('/stock/produits')}>
                                                Voir les produits
                                            </Button>
                                            <Button variant="primary" onClick={() => {
                                                setImportStep(1);
                                                setSelectedFile(null);
                                                setFilePreview([]);
                                                setValidationResults({});
                                                setImportStats({});
                                            }}>
                                                Nouvel import
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </div>
                <Footer />
            </div>

            <style>{`
                .upload-area {
                    border: 2px dashed #dee2e6;
                    border-radius: 12px;
                    background: #f8fafc;
                    transition: all 0.3s ease;
                }
                .upload-area:hover {
                    border-color: #4361ee;
                    background: white;
                }
                .step-indicator {
                    flex: 1;
                    text-align: center;
                    position: relative;
                }
                .step-circle {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: #f1f5f9;
                    color: #64748b;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 0.5rem;
                    font-weight: 600;
                }
                .step-circle.active {
                    background: #4361ee;
                    color: white;
                }
                .bg-success-soft { background: rgba(16, 185, 129, 0.1); }
                .bg-danger-soft { background: rgba(239, 68, 68, 0.1); }
                .bg-info-soft { background: rgba(59, 130, 246, 0.1); }
            `}</style>
        </React.Fragment>
    );
}